import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Send, Clock, MessageSquare, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500/20 text-blue-400 border-blue-500/50' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' },
  waiting: { label: 'Waiting', color: 'bg-purple-500/20 text-purple-400 border-purple-500/50' },
  closed: { label: 'Closed', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-zinc-500/20 text-zinc-400' },
  medium: { label: 'Medium', color: 'bg-blue-500/20 text-blue-400' },
  high: { label: 'High', color: 'bg-orange-500/20 text-orange-400' },
  urgent: { label: 'Urgent', color: 'bg-red-500/20 text-red-400' }
};

export default function SupportTicketPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [ticket, setTicket] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchTicket(); }, [ticketId]);

  const fetchTicket = async () => {
    try {
      const res = await axios.get(`${API}/my/tickets/${ticketId}`, authHeaders);
      setTicket(res.data.ticket);
      setActivities(res.data.activities || []);
    } catch (err) {
      toast.error('Failed to load ticket');
      navigate('/account');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/my/tickets/${ticketId}/reply`, { content: replyText, is_internal: false }, authHeaders);
      setReplyText('');
      toast.success('Reply sent');
      fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <Layout><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-zinc-400" /></div></Layout>;
  if (!ticket) return <Layout><div className="text-center py-20 text-muted-foreground">Ticket not found</div></Layout>;

  const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
  const priority = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;
  const isClosed = ticket.status === 'closed';

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Header */}
        <Button variant="ghost" onClick={() => navigate('/account')} className="mb-4 text-muted-foreground hover:text-foreground" data-testid="back-to-account">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Account
        </Button>

        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">{ticket.ticket_number}</p>
              <h1 className="text-xl font-bold text-foreground" data-testid="ticket-subject">{ticket.subject}</h1>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={priority.color} data-testid="ticket-priority">{priority.label}</Badge>
              <Badge className={status.color} data-testid="ticket-status">{status.label}</Badge>
            </div>
          </div>
          {ticket.description && (
            <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap" data-testid="ticket-description">{ticket.description}</p>
          )}
          <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Opened {formatDate(ticket.created_at)}
          </p>
        </div>

        {/* Conversation */}
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Conversation</h2>
        <div className="space-y-3 mb-6" data-testid="ticket-conversation">
          {activities.length === 0 ? (
            <div className="text-center py-8 bg-card border border-border rounded-lg">
              <MessageSquare className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">No messages yet. Add a reply below.</p>
            </div>
          ) : (
            activities.map((act) => {
              const isCustomer = act.created_by === user?.email;
              return (
                <div
                  key={act.id}
                  className={`p-4 rounded-lg border ${isCustomer ? 'bg-[#78BE20]/5 border-[#78BE20]/20 ml-8' : 'bg-card border-border mr-8'}`}
                  data-testid={`activity-${act.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-foreground">
                      {isCustomer ? 'You' : 'Support Team'}
                    </span>
                    <span className="text-xs text-muted-foreground">{formatDate(act.created_at)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{act.content}</p>
                </div>
              );
            })
          )}
        </div>

        {/* Reply Box */}
        {!isClosed ? (
          <div className="bg-card border border-border rounded-lg p-4" data-testid="reply-section">
            <Textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              className="min-h-[100px] bg-zinc-800 border-zinc-700 mb-3"
              data-testid="reply-input"
            />
            <div className="flex justify-end">
              <Button
                onClick={handleReply}
                disabled={sending || !replyText.trim()}
                className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                data-testid="send-reply-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send Reply
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 text-center">
            <p className="text-muted-foreground text-sm">This ticket is closed. Please open a new ticket if you need further assistance.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
